import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Upload, Sparkles, Download } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ['school_fees', 'uniform', 'books', 'scholastic_materials', 'meals', 'transportation', 'medical', 'accommodation', 'other'];

export const AgreementsTab = ({ onImported }) => {
  const [agreements, setAgreements] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', sponsor_id: '', reference_number: '', period: '', amount_committed: '', start_date: '', end_date: '', file_name: '', file_type: '', file_data: '' });
  const [review, setReview] = useState(null); // {agreement_id, rows}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API_URL}/api/sponsorship-program/agreements`).then((r) => setAgreements(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/sponsorship-program/sponsors`).then((r) => setSponsors(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error('Max 8 MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, file_name: f.name, file_type: f.type, file_data: reader.result.split(',')[1] });
    reader.readAsDataURL(f);
  };

  const save = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/sponsorship-program/agreements`, { ...form, amount_committed: parseFloat(form.amount_committed) || 0, sponsor_id: form.sponsor_id || null });
      toast.success('Agreement uploaded');
      setOpen(false); load();
      if (form.file_data) extract(r.data.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const extract = async (id) => {
    setBusy(true);
    toast.info('Analyzing document with AI…');
    try {
      const r = await axios.post(`${API_URL}/api/sponsorship-program/agreements/${id}/extract`);
      setReview({ agreement_id: id, rows: r.data.rows || [], meta: r.data });
    } catch (e) { toast.error(e.response?.data?.detail || 'Extraction failed'); }
    finally { setBusy(false); }
  };

  const confirmImport = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/sponsorship-program/agreements/import`, {
        agreement_id: review.agreement_id,
        rows: review.rows.map((x) => ({ sponsor_name: x.sponsor_name, child_name: x.child_name, amount: parseFloat(x.amount) || 0, period: x.period, sponsor_id: x.sponsor_id, child_id: x.child_id })),
      });
      toast.success(`Imported ${r.data.imported} allocation(s) — ${r.data.created_sponsors} new sponsor(s), ${r.data.created_children} new child(ren)`);
      setReview(null); load(); onImported?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
  };

  const setRow = (i, k, v) => setReview({ ...review, rows: review.rows.map((x, j) => j === i ? { ...x, [k]: v } : x) });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div><CardTitle className="font-heading">Sponsorship Agreements</CardTitle><CardDescription>Upload agreements & payment schedules — AI extracts allocations for review</CardDescription></div>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="upload-agreement-btn"><Upload className="h-4 w-4 mr-1" /> Upload Sponsorship Agreement</Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {agreements.length === 0 ? <p className="text-center py-6 text-muted-foreground text-sm">No agreements uploaded yet</p> :
          agreements.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center justify-between border rounded-lg p-3 gap-2" data-testid={`agreement-row-${a.id}`}>
              <div>
                <div className="font-medium text-sm">{a.title} <Badge variant="outline" className="ml-1 text-[10px]">{a.status}</Badge></div>
                <p className="text-xs text-muted-foreground">{a.reference_number || '—'} · {a.period || '—'} · Committed {formatCurrency(a.amount_committed || 0)} · {formatDate(a.created_at)}</p>
              </div>
              <div className="flex gap-1.5">
                {a.file_name && <Button size="sm" variant="outline" className="h-8" onClick={async () => {
                  const res = await axios.get(`${API_URL}/api/sponsorship-program/agreements/${a.id}/download`, { responseType: 'blob' });
                  const url = URL.createObjectURL(res.data); const el = document.createElement('a'); el.href = url; el.download = a.file_name; el.click(); URL.revokeObjectURL(url);
                }}><Download className="h-3.5 w-3.5" /></Button>}
                {a.file_name && <Button size="sm" variant="outline" className="h-8" disabled={busy} onClick={() => extract(a.id)} data-testid={`extract-${a.id}`}><Sparkles className="h-3.5 w-3.5 mr-1 text-amber-500" />{busy ? 'Analyzing…' : 'Extract'}</Button>}
              </div>
            </div>
          ))}
      </CardContent>

      {open && (
        <Dialog open onOpenChange={(o) => !o && setOpen(false)}>
          <DialogContent className="max-w-lg" data-testid="agreement-dialog">
            <DialogHeader><DialogTitle>Upload Sponsorship Agreement</DialogTitle>
              <DialogDescription>PDF, Excel, CSV or Word — AI will extract sponsor → child allocations for your review.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1"><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="agreement-title" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Sponsor (optional)</Label>
                  <Select value={form.sponsor_id} onValueChange={(v) => setForm({ ...form, sponsor_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{sponsors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1"><Label>Reference No.</Label><Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} /></div>
                <div className="space-y-1"><Label>Period</Label><Input placeholder="e.g. 2026" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} /></div>
                <div className="space-y-1"><Label>Amount Committed</Label><Input type="number" value={form.amount_committed} onChange={(e) => setForm({ ...form, amount_committed: e.target.value })} /></div>
              </div>
              <div className="space-y-1"><Label>Document</Label><Input type="file" accept=".pdf,.xlsx,.xls,.csv,.docx,.txt" onChange={onFile} data-testid="agreement-file" />
                {form.file_name && <Badge variant="outline">{form.file_name}</Badge>}</div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={!form.title.trim()} data-testid="agreement-save">Upload{form.file_data ? ' & Analyze' : ''}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {review && (
        <Dialog open onOpenChange={(o) => !o && setReview(null)}>
          <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="review-dialog">
            <DialogHeader><DialogTitle>Review Sponsorship Allocations</DialogTitle>
              <DialogDescription>Nothing is saved until you confirm. Edit rows, matches are suggested automatically.</DialogDescription></DialogHeader>
            <div className="space-y-2">
              {review.rows.map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center border rounded-lg p-2" data-testid={`review-row-${i}`}>
                  <Input className="col-span-3 h-8" value={r.sponsor_name || ''} onChange={(e) => setRow(i, 'sponsor_name', e.target.value)} />
                  <Input className="col-span-3 h-8" value={r.child_name || ''} onChange={(e) => setRow(i, 'child_name', e.target.value)} />
                  <Input className="col-span-2 h-8" type="number" value={r.amount} onChange={(e) => setRow(i, 'amount', e.target.value)} />
                  <Input className="col-span-2 h-8" value={r.period || ''} onChange={(e) => setRow(i, 'period', e.target.value)} />
                  <div className="col-span-1"><Badge variant="outline" className={r.status === 'matched' ? 'text-green-600' : r.status === 'partial' ? 'text-amber-600' : 'text-blue-600'}>{r.status === 'matched' ? 'Matched' : r.status === 'partial' ? 'Partial' : 'New'}</Badge></div>
                  <Button size="sm" variant="ghost" className="col-span-1 h-8" onClick={() => setReview({ ...review, rows: review.rows.filter((_, j) => j !== i) })}>✕</Button>
                  {(r.sponsor_match || r.child_match) && <p className="col-span-12 text-xs text-muted-foreground -mt-1">Matches: {r.sponsor_match ? `sponsor "${r.sponsor_match}"` : ''} {r.child_match ? `· child "${r.child_match}"` : ''}</p>}
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => setReview({ ...review, rows: [...review.rows, { sponsor_name: '', child_name: '', amount: 0, period: '', status: 'new' }] })}>+ Add Row</Button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReview(null)}>Cancel</Button>
              <Button onClick={confirmImport} disabled={review.rows.length === 0} data-testid="confirm-import-btn">Confirm & Import ({review.rows.length})</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};

export const BulkExpenseDialog = ({ children, onDone }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: '', total_amount: '', date: new Date().toISOString().slice(0, 10), description: '', payee: '', payment_method: '', reference_number: '' });
  const [selected, setSelected] = useState({}); // child_id -> amount
  const [schoolFilter, setSchoolFilter] = useState('');

  const shown = children.filter((c) => !schoolFilter || (c.school || '').toLowerCase().includes(schoolFilter.toLowerCase()));
  const ids = Object.keys(selected);
  const total = parseFloat(form.total_amount) || 0;
  const allocTotal = ids.reduce((s, id) => s + (parseFloat(selected[id]) || 0), 0);

  const equalSplit = () => {
    if (!ids.length || !total) return;
    const each = Math.floor(total / ids.length);
    const next = {};
    ids.forEach((id, i) => { next[id] = i === 0 ? total - each * (ids.length - 1) : each; });
    setSelected(next);
  };

  const submit = async () => {
    try {
      await axios.post(`${API_URL}/api/sponsorship-program/expenses/bulk`, {
        ...form, total_amount: total,
        allocations: ids.map((id) => ({ child_id: id, amount: parseFloat(selected[id]) || 0 })),
      });
      toast.success(`Bulk expense recorded — one Finance transaction, ${ids.length} child allocations`);
      setOpen(false); setSelected({}); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="bulk-expense-btn">Record Bulk Expense</Button>
      {open && (
        <Dialog open onOpenChange={(o) => !o && setOpen(false)}>
          <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="bulk-expense-dialog">
            <DialogHeader><DialogTitle>Record Bulk Expense</DialogTitle>
              <DialogDescription>One Finance transaction, split across children for accountability.</DialogDescription></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="bulk-category"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1"><Label>Total Amount (UGX) *</Label><Input type="number" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} data-testid="bulk-total" /></div>
              <div className="space-y-1"><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div className="space-y-1"><Label>Supplier / Payee</Label><Input value={form.payee} onChange={(e) => setForm({ ...form, payee: e.target.value })} data-testid="bulk-payee" /></div>
              <div className="space-y-1"><Label>Payment Method</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{['cash', 'mobile_money', 'bank_transfer', 'cheque'].map((m) => <SelectItem key={m} value={m}>{m.replace('_', ' ')}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1"><Label>Reference No.</Label><Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} /></div>
              <div className="space-y-1 col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>
            <div className="border-t pt-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Select Children ({ids.length} selected)</Label>
                <div className="flex gap-2">
                  <Input className="h-8 w-36" placeholder="Filter by school" value={schoolFilter} onChange={(e) => setSchoolFilter(e.target.value)} />
                  <Button size="sm" variant="outline" className="h-8" onClick={() => {
                    const next = {}; shown.forEach((c) => { next[c.id] = selected[c.id] || ''; }); setSelected(next);
                  }} data-testid="select-all-children">Select All</Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={equalSplit} data-testid="bulk-equal-split">Equal Split</Button>
                </div>
              </div>
              <div className="max-h-56 overflow-y-auto space-y-1">
                {shown.map((c) => (
                  <div key={c.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={c.id in selected} onCheckedChange={(v) => {
                      const next = { ...selected };
                      if (v) next[c.id] = next[c.id] || ''; else delete next[c.id];
                      setSelected(next);
                    }} data-testid={`bulk-child-${c.id}`} />
                    <span className="flex-1 truncate">{c.name} <span className="text-xs text-muted-foreground">{c.school || ''} {c.grade || ''}</span></span>
                    {c.id in selected && <Input type="number" className="w-32 h-7" placeholder="Amount" value={selected[c.id]} onChange={(e) => setSelected({ ...selected, [c.id]: e.target.value })} />}
                  </div>
                ))}
              </div>
              {ids.length > 0 && (
                <p className={`text-sm font-medium ${Math.abs(allocTotal - total) < 0.01 && total > 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="bulk-alloc-total">
                  Allocated {formatCurrency(allocTotal)} of {formatCurrency(total)}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={!form.category || !total || !ids.length || Math.abs(allocTotal - total) > 0.01} data-testid="bulk-confirm">Confirm Bulk Expense</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export const ProgramAlerts = () => {
  const [summary, setSummary] = useState(null);
  useEffect(() => { axios.get(`${API_URL}/api/sponsorship-program/summary`).then((r) => setSummary(r.data)).catch(() => {}); }, []);
  if (!summary) return null;
  return (
    <Card data-testid="sponsorship-alerts">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-base">Program Alerts & Financial Summary</CardTitle>
        <CardDescription>
          Donations {formatCurrency(summary.total_donations)} − Expenses {formatCurrency(summary.total_child_expenses)} = <strong>Remaining {formatCurrency(summary.remaining_funds)}</strong> · Unallocated {formatCurrency(summary.unallocated_donations)} · {summary.active_sponsors} active sponsor(s) · {summary.active_agreements} agreement(s)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {summary.alerts.length === 0 ? <p className="text-sm text-muted-foreground">No alerts — everything looks good.</p> :
          summary.alerts.map((a, i) => (
            <div key={i} className={`border rounded-lg p-2.5 text-sm ${a.severity === 'critical' ? 'border-red-300 bg-red-50 dark:bg-red-900/15' : a.severity === 'warning' ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/15' : 'border-blue-200 bg-blue-50 dark:bg-blue-900/15'}`}>
              <p className="font-medium">{a.title}</p>
              <p className="text-xs text-muted-foreground">{a.detail}</p>
            </div>
          ))}
      </CardContent>
    </Card>
  );
};
